import { useEffect, useMemo, useState } from "react";
import {
  TableWrapper,
  TableBody,
  TableRow,
  TableRowItem,
  TableFooter,
  TablePagination,
} from "@/components/ui/table";

import { AdminInvoice } from "@/queries/admin/invoices";

const SORTABLE_FIELDS = [
  "customer",
  "status",
  "amount",
  "dateSentTimestamp",
  "opportunity",
] as const;
export type SortableField = (typeof SORTABLE_FIELDS)[number];

const ENTRIES_PER_PAGE = 10;

const InvoiceTable = ({ invoices }: { invoices: AdminInvoice[] }) => {
  const [page, setPage] = useState(1);
  const [sortField, setSortField] =
    useState<SortableField>("dateSentTimestamp");
  const [sortDirection, setSortDirection] = useState<1 | -1>(-1);

  const sortedEntries: AdminInvoice[] | undefined = useMemo(() => {
    switch (sortField) {
      case "customer":
      case "status":
        return [...invoices].sort(
          (a, b) =>
            (a[sortField] ?? "").localeCompare(b[sortField] ?? "") *
            sortDirection,
        );
      case "amount":
      case "dateSentTimestamp":
        return [...invoices].sort(
          (a, b) => ((a[sortField] ?? 0) - (b[sortField] ?? 0)) * sortDirection,
        );
      case "opportunity":
        return [...invoices].sort(
          (a, b) =>
            (a.relatedOpportunity?.name ?? "").localeCompare(
              b.relatedOpportunity?.name ?? "",
            ) * sortDirection,
        );
    }
  }, [invoices, sortField, sortDirection]);

  const numberOfPages = useMemo(() => {
    if (!sortedEntries) {
      return 1;
    }

    return Math.ceil(sortedEntries.length / ENTRIES_PER_PAGE);
  }, [sortedEntries]);

  const currentPageEntries = useMemo(
    () =>
      sortedEntries?.slice(
        (page - 1) * ENTRIES_PER_PAGE,
        page * ENTRIES_PER_PAGE,
      ),
    [sortedEntries, page],
  );

  const onHeadingClick = (field: SortableField) => {
    if (field !== sortField) {
      setSortField(field);
      setSortDirection(1);
    } else {
      setSortDirection((prev) => (prev === 1 ? -1 : 1));
    }
  };

  useEffect(() => {
    if (page > numberOfPages) {
      setPage(numberOfPages || 1);
    }
  }, [page, numberOfPages]);

  return (
    <div className="py-6 self-stretch flex flex-col lg:flex-row gap-x-6 gap-y-2">
      <div className="flex-1 min-w-0">
        <TableWrapper>
          <TableBody>
            <thead>
              <TableRow>
                <TableRowItem
                  heading
                  onClick={() => onHeadingClick("customer")}
                  sortDirection={
                    sortField === "customer" ? sortDirection : undefined
                  }
                >
                  Customer
                </TableRowItem>
                <TableRowItem
                  heading
                  onClick={() => onHeadingClick("status")}
                  sortDirection={
                    sortField === "status" ? sortDirection : undefined
                  }
                >
                  Status
                </TableRowItem>
                <TableRowItem
                  heading
                  onClick={() => onHeadingClick("amount")}
                  sortDirection={
                    sortField === "amount" ? sortDirection : undefined
                  }
                >
                  Amount
                </TableRowItem>
                <TableRowItem
                  heading
                  onClick={() => onHeadingClick("opportunity")}
                  sortDirection={
                    sortField === "opportunity" ? sortDirection : undefined
                  }
                >
                  Related Opportunity
                </TableRowItem>
                <TableRowItem
                  heading
                  onClick={() => onHeadingClick("dateSentTimestamp")}
                  sortDirection={
                    sortField === "dateSentTimestamp"
                      ? sortDirection
                      : undefined
                  }
                >
                  Date Issued
                </TableRowItem>
                {/* <TableRowItem heading>Invoice</TableRowItem> */}
              </TableRow>
            </thead>
            <tbody>
              {currentPageEntries?.map((invoice) => (
                <AdminRow key={`invoice-${invoice.id}`} invoice={invoice} />
              ))}
            </tbody>
          </TableBody>
          {numberOfPages > 1 ? (
            <TableFooter>
              <TablePagination
                currentPage={page}
                totalPages={numberOfPages}
                navigateFn={setPage}
              />
            </TableFooter>
          ) : null}
        </TableWrapper>
      </div>
    </div>
  );
};

const formatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const AdminRow = ({ invoice }: { invoice: AdminInvoice }) => {
  return (
    <TableRow>
      <TableRowItem>
        <span className="text-[#5C6060] text-sm">{invoice.customer}</span>
      </TableRowItem>
      <TableRowItem>
        <span className="text-[#5C6060] text-sm">{invoice.status}</span>
      </TableRowItem>
      <TableRowItem>
        <span className="text-[#5C6060] text-sm">
          {formatter.format(invoice.amount / 100)}
        </span>
      </TableRowItem>
      <TableRowItem>
        {invoice.relatedOpportunity?.id ? (
          <a
            href={`/new-admin/job/${invoice.relatedOpportunity.id}`}
            className="text-xs text-[#646cff] font-medium hover:underline"
          >
            {invoice.relatedOpportunity?.name ?? "Unnamed"}
          </a>
        ) : (
          <span className="text-[#5C6060] text-sm">
            {invoice.relatedOpportunity?.name ?? ""}
          </span>
        )}
      </TableRowItem>
      <TableRowItem>
        <span className="text-[#5C6060] text-sm">
          {invoice.dateSentTimestamp
            ? new Date(invoice.dateSentTimestamp).toLocaleDateString("en-US", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })
            : "Unknown"}
        </span>
      </TableRowItem>
      {/* <TableRowItem>
        <a
          href={invoice.invoiceLink}
          target="_blank"
          rel="noreferrer"
          className="flex gap-0.5 text-xs text-[#646cff] font-medium hover:underline"
        >
          Invoice
          <ExternalLink size={16} />
        </a>
      </TableRowItem> */}
    </TableRow>
  );
};

export default InvoiceTable;
